// 051 parte (d): tablas CONTROLES PRENATALES (5 renglones) y POSTPARTO (8 renglones) del papel. Cada renglón es un grupo con todas
// las columnas impresas; los renglones sin datos no se imprimen (ver `bloques051ControlesDatos.ts`).
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CampoDictado } from './CampoDictado';
import { SiNoBotones } from './camposSimples';
import {
  COLUMNAS_POSTPARTO,
  COLUMNAS_PRENATAL,
  type EstadoControlesPrenatales051,
  type EstadoPostparto051,
  type FilaControlPrenatal051,
  type FilaPostparto051,
} from './bloques051ControlesDatos';

type TipoCampo = 'text' | 'date' | 'time';

// Constantes vitales, medidas cortas y códigos (no texto libre real): sin dictado.
const COLUMNAS_ESTRUCTURADAS = new Set<string>(['edadGestacional', 'peso', 'ta', 'fc', 'fr', 'temperatura', 'saturacion', 'proteinuria', 'alturaUterina', 'frecuenciaCardiacaFetal', 'iniciales', 'pulso']);

function Campo({
  id,
  label,
  tipo = 'text',
  dictado = false,
  value,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly tipo?: TipoCampo;
  readonly dictado?: boolean;
  readonly value: string;
  readonly onChange: (v: string) => void;
}): JSX.Element {
  if (dictado) {
    return <CampoDictado id={id} etiqueta={label} valor={value} onChange={onChange} />;
  }
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={tipo} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

const TIPO_PRENATAL: Partial<Record<(typeof COLUMNAS_PRENATAL)[number], TipoCampo>> = { fecha: 'date', proximaCita: 'date' };
const TIPO_POSTPARTO: Partial<Record<(typeof COLUMNAS_POSTPARTO)[number], TipoCampo>> = { dia: 'date', hora: 'time' };

export function BloqueControlesPrenatales({ estado, onChange }: { readonly estado: EstadoControlesPrenatales051; readonly onChange: (v: EstadoControlesPrenatales051) => void }): JSX.Element {
  const { t } = useTranslation();
  const P = 'formularios.f051.controlPrenatal';
  function actualizarFila(i: number, cambios: Partial<FilaControlPrenatal051>): void {
    onChange({ filas: estado.filas.map((f, j) => (j === i ? { ...f, ...cambios } : f)) });
  }
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t(`${P}.titulo`)}</h4>
      {estado.filas.map((fila, i) => (
        <fieldset key={i} id={`f051-cp-${i + 1}`} className="flex min-w-0 flex-col gap-2 rounded-md border border-hce-line p-3">
          <legend className="px-1 text-sm font-medium">{`${t(`${P}.filaTitulo`)} ${i + 1}`}</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
            {COLUMNAS_PRENATAL.map((c) => {
              const id = `f051-cp-${i + 1}-${c}`;
              if (c === 'gananciaAdecuadaPeso' || c === 'sangradoVaginal') {
                return <SiNoBotones key={c} id={id} etiqueta={t(`${P}.${c}`)} valor={fila[c]} onChange={(v) => actualizarFila(i, { [c]: v })} textos={{ si: t('formulario.si'), no: t('formulario.no') }} />;
              }
              const tipo = TIPO_PRENATAL[c] ?? 'text';
              return <Campo key={c} id={id} label={t(`${P}.${c}`)} tipo={tipo} dictado={tipo === 'text' && !COLUMNAS_ESTRUCTURADAS.has(c)} value={fila[c]} onChange={(v) => actualizarFila(i, { [c]: v })} />;
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

export function BloquePostparto({ estado, onChange }: { readonly estado: EstadoPostparto051; readonly onChange: (v: EstadoPostparto051) => void }): JSX.Element {
  const { t } = useTranslation();
  const P = 'formularios.f051.postparto';
  function actualizarFila(i: number, cambios: Partial<FilaPostparto051>): void {
    onChange({ filas: estado.filas.map((f, j) => (j === i ? { ...f, ...cambios } : f)) });
  }
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t(`${P}.titulo`)}</h4>
      {estado.filas.map((fila, i) => (
        <fieldset key={i} id={`f051-pp-${i + 1}`} className="flex min-w-0 flex-col gap-2 rounded-md border border-hce-line p-3">
          <legend className="px-1 text-sm font-medium">{`${t(`${P}.filaTitulo`)} ${i + 1}`}</legend>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
            {COLUMNAS_POSTPARTO.map((c) => {
              const tipo = TIPO_POSTPARTO[c] ?? 'text';
              return (
                <Campo
                  key={c}
                  id={`f051-pp-${i + 1}-${c}`}
                  label={t(`${P}.${c}`)}
                  tipo={tipo}
                  dictado={tipo === 'text' && !COLUMNAS_ESTRUCTURADAS.has(c)}
                  value={fila[c]}
                  onChange={(v) => actualizarFila(i, { [c]: v })}
                />
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
